import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { Typography, Card, CardContent, TextField, Grid } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CancelIcon from "@mui/icons-material/Cancel";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { useMaterialUIController } from "context";
import {
  FP,
  PageHeader,
  StatCard,
  fpInputSx,
} from "components/financepro";

const parseNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).split(",").join(""));
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) =>
  `$${parseNumber(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

const addMonths = (date, n) => {
  const d = new Date(date.getTime());
  const targetMonth = d.getMonth() + n;
  d.setMonth(targetMonth);
  return d;
};

const todayISO = () => {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

function CalculadoraPrestamos() {
  const [monto, setMonto] = useState("");
  const [tasa, setTasa] = useState("");
  const [plazo, setPlazo] = useState("");
  const [fechaInicio, setFechaInicio] = useState(todayISO());
  const [calculado, setCalculado] = useState(false);
  const [presentarpdf, setPresentarpdf] = useState(false);

  const [controller] = useMaterialUIController();
  const { nombreNegocio } = controller;
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const resultado = useMemo(() => {
    const P = parseNumber(monto);
    const r = parseNumber(tasa) / 100;
    const n = Math.floor(parseNumber(plazo));
    if (P <= 0 || r < 0 || n <= 0) {
      return { cuota: 0, interesMensual: 0, capitalMensual: 0, totalPagar: 0, totalIntereses: 0, tabla: [] };
    }
    // Amortización simple: capital fijo + interés simple sobre el capital original.
    const capitalMensual = P / n;
    const interesMensual = P * r;
    const cuota = capitalMensual + interesMensual;

    const inicio = fechaInicio ? new Date(`${fechaInicio}T00:00:00`) : new Date();
    let saldo = P;
    const tabla = [];
    for (let i = 1; i <= n; i++) {
      saldo = Math.max(0, saldo - capitalMensual);
      tabla.push({
        nro: i,
        fecha: addMonths(inicio, i),
        capital: capitalMensual,
        interes: interesMensual,
        cuota: cuota,
        saldo: i === n ? 0 : saldo,
      });
    }
    return {
      cuota,
      interesMensual,
      capitalMensual,
      totalPagar: cuota * n,
      totalIntereses: interesMensual * n,
      tabla,
    };
  }, [monto, tasa, plazo, fechaInicio]);

  const valido = parseNumber(monto) > 0 && parseNumber(tasa) >= 0 && Math.floor(parseNumber(plazo)) > 0;

  const handleCalcular = () => {
    if (valido) setCalculado(true);
  };

  const handleLimpiar = () => {
    setMonto("");
    setTasa("");
    setPlazo("");
    setFechaInicio(todayISO());
    setCalculado(false);
  };

  const MyDocument = () => (
    <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%" }}>
      <Document>
        <Page size="A4">
          <View style={{ textAlign: "center", width: "94%", fontSize: 14, textTransform: "uppercase", marginTop: 15 }} fixed>
            <Text>{nombreNegocio}</Text>
          </View>
          <View style={{ textAlign: "center", width: "94%", fontSize: 10, textTransform: "uppercase" }} fixed>
            <Text>Cálculo de Préstamo — Amortización Simple</Text>
          </View>
          <View style={{ textAlign: "center", width: "94%", fontSize: 9, marginBottom: 6 }} fixed>
            <Text>  </Text>
          </View>

          <View style={{ marginLeft: 20, marginRight: 20, marginBottom: 10, fontSize: 9 }}>
            <View style={{ flexDirection: "row", marginBottom: 2 }}>
              <Text style={{ width: 140, fontWeight: "bold" }}>Monto del préstamo:</Text>
              <Text>{fmtMoney(monto)}</Text>
            </View>
            <View style={{ flexDirection: "row", marginBottom: 2 }}>
              <Text style={{ width: 140, fontWeight: "bold" }}>Tasa de interés mensual:</Text>
              <Text>{parseNumber(tasa).toFixed(2)}%</Text>
            </View>
            <View style={{ flexDirection: "row", marginBottom: 2 }}>
              <Text style={{ width: 140, fontWeight: "bold" }}>Plazo (meses):</Text>
              <Text>{Math.floor(parseNumber(plazo))}</Text>
            </View>
            <View style={{ flexDirection: "row", marginBottom: 2 }}>
              <Text style={{ width: 140, fontWeight: "bold" }}>Cuota mensual:</Text>
              <Text>{fmtMoney(resultado.cuota)}</Text>
            </View>
            <View style={{ flexDirection: "row", marginBottom: 2 }}>
              <Text style={{ width: 140, fontWeight: "bold" }}>Total a pagar:</Text>
              <Text>{fmtMoney(resultado.totalPagar)}</Text>
            </View>
            <View style={{ flexDirection: "row", marginBottom: 2 }}>
              <Text style={{ width: 140, fontWeight: "bold" }}>Total intereses:</Text>
              <Text>{fmtMoney(resultado.totalIntereses)}</Text>
            </View>
          </View>

          <View style={{ marginLeft: 20, marginRight: 20 }}>
            <View style={{ flexDirection: "row", height: 18, fontSize: 9, backgroundColor: "#E6EAF1", color: "black" }}>
              <Text style={{ width: 30, padding: 2, textAlign: "center" }}>#</Text>
              <Text style={{ width: 75, padding: 2 }}>Fecha</Text>
              <Text style={{ width: 90, padding: 2, textAlign: "right" }}>Capital</Text>
              <Text style={{ width: 90, padding: 2, textAlign: "right" }}>Interés</Text>
              <Text style={{ width: 100, padding: 2, textAlign: "right" }}>Cuota</Text>
              <Text style={{ width: 100, padding: 2, textAlign: "right" }}>Saldo</Text>
            </View>
            {resultado.tabla.map((r) => (
              <View key={r.nro} style={{ flexDirection: "row", height: 14, fontSize: 8, color: "black" }}>
                <Text style={{ width: 30, padding: 2, textAlign: "center" }}>{r.nro}</Text>
                <Text style={{ width: 75, padding: 2 }}>{fmtDate(r.fecha)}</Text>
                <Text style={{ width: 90, padding: 2, textAlign: "right" }}>{fmtMoney(r.capital)}</Text>
                <Text style={{ width: 90, padding: 2, textAlign: "right" }}>{fmtMoney(r.interes)}</Text>
                <Text style={{ width: 100, padding: 2, textAlign: "right" }}>{fmtMoney(r.cuota)}</Text>
                <Text style={{ width: 100, padding: 2, textAlign: "right" }}>{fmtMoney(r.saldo)}</Text>
              </View>
            ))}
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Préstamos"
        subtitle="Calculadora de Préstamos"
        cta={
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            disabled={!calculado || resultado.tabla.length === 0}
            onClick={() => setPresentarpdf(true)}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Exportar PDF
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TuneRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Datos del Préstamo
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Monto del préstamo"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Tasa interés mensual (%)"
                  value={tasa}
                  onChange={(e) => setTasa(e.target.value)}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Plazo (meses)"
                  value={plazo}
                  onChange={(e) => setPlazo(e.target.value)}
                  fullWidth
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Fecha de inicio"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" gap={1.2} sx={{ mt: 2.5, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                startIcon={<RestartAltRoundedIcon />}
                onClick={handleLimpiar}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 140 },
                  height: 46,
                  borderColor: FP.color.borderStrong,
                  color: FP.color.textSecondary,
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  "&:hover": { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.navy, color: FP.color.navy },
                }}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                disabled={!valido}
                startIcon={<CalculateRoundedIcon />}
                onClick={handleCalcular}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                }}
              >
                Calcular
              </Button>
            </Box>
          </CardContent>
        </Card>

        {calculado && resultado.tabla.length > 0 && (
          <>
            <Box
              sx={{
                display: "grid",
                width: "100%",
                gap: 2,
                mb: 3,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              <StatCard label="Cuota mensual" value={fmtMoney(resultado.cuota)} icon={PaidRoundedIcon} accent={FP.color.accentBlue} />
              <StatCard label="Interés mensual" value={fmtMoney(resultado.interesMensual)} icon={PercentRoundedIcon} accent={FP.color.warning} />
              <StatCard label="Total a pagar" value={fmtMoney(resultado.totalPagar)} icon={AccountBalanceRoundedIcon} accent={FP.color.navy} />
              <StatCard label="Total intereses" value={fmtMoney(resultado.totalIntereses)} icon={EventRoundedIcon} accent={FP.color.danger} />
            </Box>

            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${FP.color.border}`,
                bgcolor: FP.color.cardBg,
                boxShadow: FP.shadow.soft,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1.6,
                  borderBottom: `1px solid ${FP.color.border}`,
                  bgcolor: "#FAFBFE",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                }}
              >
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
                  Tabla de Amortización
                </Typography>
                <Typography sx={{ ml: "auto", color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>
                  {resultado.tabla.length} cuotas
                </Typography>
              </Box>
              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ minWidth: 640 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "60px 1.2fr 1.4fr 1.4fr 1.4fr 1.4fr",
                      bgcolor: "#F4F6FB",
                      borderBottom: `1px solid ${FP.color.border}`,
                      px: 2,
                      py: 1.1,
                      color: FP.color.textSecondary,
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    <Box>#</Box>
                    <Box>Fecha</Box>
                    <Box sx={{ textAlign: "right" }}>Capital</Box>
                    <Box sx={{ textAlign: "right" }}>Interés</Box>
                    <Box sx={{ textAlign: "right" }}>Cuota</Box>
                    <Box sx={{ textAlign: "right" }}>Saldo</Box>
                  </Box>
                  {resultado.tabla.map((row, idx) => (
                    <Box
                      key={row.nro}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "60px 1.2fr 1.4fr 1.4fr 1.4fr 1.4fr",
                        px: 2,
                        py: 1,
                        borderBottom: idx === resultado.tabla.length - 1 ? "none" : `1px solid ${FP.color.border}`,
                        bgcolor: idx % 2 === 0 ? "#FFFFFF" : "#FAFBFE",
                        fontSize: "0.86rem",
                        color: FP.color.textPrimary,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      <Box sx={{ fontWeight: 700 }}>{row.nro}</Box>
                      <Box>{fmtDate(row.fecha)}</Box>
                      <Box sx={{ textAlign: "right" }}>{fmtMoney(row.capital)}</Box>
                      <Box sx={{ textAlign: "right" }}>{fmtMoney(row.interes)}</Box>
                      <Box sx={{ textAlign: "right", fontWeight: 700 }}>{fmtMoney(row.cuota)}</Box>
                      <Box sx={{ textAlign: "right" }}>{fmtMoney(row.saldo)}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Card>
          </>
        )}

        {!calculado && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <CalculateRoundedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  Calculadora de Préstamos
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                  Ingrese el monto, la tasa de interés mensual y el plazo en meses para obtener la tabla de amortización simple.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {presentarpdf === true && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Cálculo
              </Typography>
              <IconButton onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                <CancelIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <MyDocument />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}

export default CalculadoraPrestamos;
